import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import {
    ArrowLeft,
    Save,
    Building2,
    Receipt,
    FileText,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Edit3,
    X,
} from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import AdminInvoiceService from "../../services/admin/AdminInvoiceService";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

export default function InvoiceSettingsPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { hasPermission } = useAdminPermissions();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [settings, setSettings] = useState({
        bank_name: "",
        account_name: "",
        account_number: "",
        cancellation_fee: "",
        deposit_fee: "",
        service_charge_percent: "",
        vat_percent: "",
        default_notes: "",
        payment_instructions: ""
    });

    const [originalSettings, setOriginalSettings] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!hasPermission("super_admin")) {
            showNotification({
                type: "failed",
                title: "Access Restricted",
                message: "You do not have permission to access invoice settings.",
            });
            navigate("/admin/dashboard");
        }
    }, [hasPermission, navigate, showNotification]);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const result = await AdminInvoiceService.getSettings();
            if (result.status === "success") {
                const data = result.data || {
                    bank_name: "",
                    account_name: "",
                    account_number: "",
                    cancellation_fee: "",
                    deposit_fee: "",
                    service_charge_percent: "",
                    vat_percent: "",
                    default_notes: "",
                    payment_instructions: ""
                };
                setSettings(data);
                setOriginalSettings(data);
            }
        } catch (error) {
            console.error("Failed to fetch settings:", error);
            showNotification({
                type: "failed",
                title: "Load Error",
                message: "Failed to load invoice settings. Please try again."
            });
        } finally {
            setIsLoading(false);
        }
    };

    const validate = () => {
        const newErrors = {};
        const requiredFields = [
            "bank_name", "account_name", "account_number",
            "cancellation_fee", "deposit_fee",
            "service_charge_percent", "vat_percent",
            "payment_instructions", "default_notes"
        ];

        requiredFields.forEach(field => {
            if (!settings[field] || String(settings[field]).trim() === "") {
                newErrors[field] = "This field is required";
            }
        });

        if (settings.account_number && (!/^\d{10}$/.test(settings.account_number))) {
            newErrors.account_number = "Account number must be exactly 10 digits";
        }

        if (settings.cancellation_fee && (settings.cancellation_fee < 0 || settings.cancellation_fee > 100)) {
            newErrors.cancellation_fee = "Must be between 0 and 100";
        }

        if (settings.deposit_fee && settings.deposit_fee < 0) {
            newErrors.deposit_fee = "Cannot be negative";
        }

        if (settings.service_charge_percent && (settings.service_charge_percent < 0 || settings.service_charge_percent > 100)) {
            newErrors.service_charge_percent = "Must be between 0 and 100";
        }

        if (settings.vat_percent && (settings.vat_percent < 0 || settings.vat_percent > 100)) {
            newErrors.vat_percent = "Must be between 0 and 100";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            showNotification({
                type: "failed",
                title: "Validation Error",
                message: "Please fill in all required fields correctly."
            });
            return;
        }

        setIsSaving(true);
        try {
            const response = await AdminInvoiceService.updateSettings(settings);
            if (response.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Settings Saved",
                    message: "Invoice settings have been updated successfully."
                });
                setOriginalSettings({ ...settings });
                setIsEditing(false);
            }
        } catch (error) {
            const message = error.response?.data?.message || "Failed to save settings. Please try again.";
            showNotification({
                type: "failed",
                title: "Save Error",
                message: message
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        const resetState = originalSettings || {
            bank_name: "",
            account_name: "",
            account_number: "",
            cancellation_fee: "",
            deposit_fee: "",
            service_charge_percent: "",
            vat_percent: "",
            default_notes: "",
            payment_instructions: ""
        };
        setSettings({ ...resetState });
        setIsEditing(false);
        setErrors({});
    };

    const inputClass = (fieldName) => `w-full px-4 py-2.5 bg-[#F9FAFB] dark:bg-[#262626] border ${errors[fieldName] ? "border-red-500" : "border-[#E5E7EB] dark:border-[#333333]"} rounded-xl text-sm focus:ring-2 focus:ring-[#62397f] outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed text-[#111111] dark:text-white`;
    const labelClass = "text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase";

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="p-4 lg:p-8 max-w-[1000px] mx-auto space-y-8">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-[#262626] rounded-xl animate-pulse"></div>
                        <div className="space-y-2">
                            <div className="h-6 w-48 bg-gray-200 dark:bg-[#262626] rounded animate-pulse"></div>
                            <div className="h-4 w-64 bg-gray-200 dark:bg-[#262626] rounded animate-pulse"></div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="h-64 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] animate-pulse"></div>
                        <div className="h-64 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] animate-pulse"></div>
                        <div className="lg:col-span-2 h-80 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] animate-pulse"></div>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[1000px] mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate("/admin/invoices")}
                            className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-xl transition-colors"
                        >
                            <ArrowLeft size={20} className="text-[#6B7280] dark:text-[#A0A0A0]" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                                Invoice Settings
                            </h1>
                            <p className="text-[#666666] dark:text-[#A0A0A0] text-sm font-inter">
                                Configure global invoice preferences and payment details
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {!isEditing ? (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#4B5563] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] rounded-xl font-bold transition-all active:scale-95 shadow-sm"
                            >
                                <Edit3 size={18} />
                                Edit Settings
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleCancel}
                                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#EF4444] hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl font-bold transition-all active:scale-95"
                                >
                                    <X size={18} />
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] disabled:opacity-60 text-white rounded-xl font-bold transition-all active:scale-95 shadow-lg shadow-purple-200 dark:shadow-none"
                                >
                                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                    {isSaving ? "Saving..." : "Save Changes"}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Bank Details */}
                    <section className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/10 rounded-xl flex items-center justify-center text-blue-600">
                                <Building2 size={20} />
                            </div>
                            <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                Payment Details
                            </h2>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <label className={labelClass}>Bank Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Zenith Bank"
                                    value={settings.bank_name || ""}
                                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                                    disabled={!isEditing}
                                    className={inputClass("bank_name")}
                                />
                                {errors.bank_name && <p className="text-[10px] text-red-500">{errors.bank_name}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Account Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. St. Stephen's Rentals"
                                    value={settings.account_name || ""}
                                    onChange={(e) => setSettings({ ...settings, account_name: e.target.value })}
                                    disabled={!isEditing}
                                    className={inputClass("account_name")}
                                />
                                {errors.account_name && <p className="text-[10px] text-red-500">{errors.account_name}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Account Number</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 1234567890"
                                    value={settings.account_number || ""}
                                    onChange={(e) => setSettings({ ...settings, account_number: e.target.value })}
                                    disabled={!isEditing}
                                    className={inputClass("account_number")}
                                />
                                {errors.account_number && <p className="text-[10px] text-red-500">{errors.account_number}</p>}
                            </div>
                        </div>
                    </section>

                    {/* Fees */}
                    <section className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/10 rounded-xl flex items-center justify-center text-purple-600">
                                <Receipt size={20} />
                            </div>
                            <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                Fees & Charges
                            </h2>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <label className={labelClass}>Cancellation Fee (%)</label>
                                <input
                                    type="number"
                                    placeholder="e.g. 20"
                                    value={settings.cancellation_fee || ""}
                                    onChange={(e) => setSettings({ ...settings, cancellation_fee: e.target.value })}
                                    disabled={!isEditing}
                                    className={inputClass("cancellation_fee")}
                                />
                                {errors.cancellation_fee && <p className="text-[10px] text-red-500">{errors.cancellation_fee}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Security Deposit Fee (₦)</label>
                                <input
                                    type="number"
                                    placeholder="e.g. 5000"
                                    value={settings.deposit_fee || ""}
                                    onChange={(e) => setSettings({ ...settings, deposit_fee: e.target.value })}
                                    disabled={!isEditing}
                                    className={inputClass("deposit_fee")}
                                />
                                {errors.deposit_fee && <p className="text-[10px] text-red-500">{errors.deposit_fee}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Service Charge (%)</label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 10"
                                        value={settings.service_charge_percent || ""}
                                        onChange={(e) => setSettings({ ...settings, service_charge_percent: e.target.value })}
                                        disabled={!isEditing}
                                        className={inputClass("service_charge_percent")}
                                    />
                                    {errors.service_charge_percent && <p className="text-[10px] text-red-500">{errors.service_charge_percent}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>VAT (%)</label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 7.5"
                                        value={settings.vat_percent || ""}
                                        onChange={(e) => setSettings({ ...settings, vat_percent: e.target.value })}
                                        disabled={!isEditing}
                                        className={inputClass("vat_percent")}
                                    />
                                    {errors.vat_percent && <p className="text-[10px] text-red-500">{errors.vat_percent}</p>}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Instructions & Notes */}
                    <section className="lg:col-span-2 bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/10 rounded-xl flex items-center justify-center text-orange-600">
                                <FileText size={20} />
                            </div>
                            <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                                Instructions & Default Notes
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1.5">
                                <label className={labelClass}>Payment Instructions</label>
                                <textarea
                                    rows="4"
                                    placeholder="Steps for payment..."
                                    value={settings.payment_instructions || ""}
                                    onChange={(e) => setSettings({ ...settings, payment_instructions: e.target.value })}
                                    disabled={!isEditing}
                                    className={`${inputClass("payment_instructions")} resize-none`}
                                ></textarea>
                                {errors.payment_instructions && <p className="text-[10px] text-red-500">{errors.payment_instructions}</p>}
                                <p className="text-[10px] text-[#9CA3AF]">This will appear in the "How to Pay" section of the invoice.</p>
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Default Invoice Notes</label>
                                <textarea
                                    rows="4"
                                    placeholder="Terms, conditions, etc..."
                                    value={settings.default_notes || ""}
                                    onChange={(e) => setSettings({ ...settings, default_notes: e.target.value })}
                                    disabled={!isEditing}
                                    className={`${inputClass("default_notes")} resize-none`}
                                ></textarea>
                                {errors.default_notes && <p className="text-[10px] text-red-500">{errors.default_notes}</p>}
                                <p className="text-[10px] text-[#9CA3AF]">These notes will be automatically added to new invoices.</p>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Last updated info */}
                {settings.updated_at && (
                    <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-[#9CA3AF]">
                        <CheckCircle2 size={12} />
                        <span>Last updated by <b>{settings.updated_by}</b> on {new Date(settings.updated_at).toLocaleString()}</span>
                    </div>
                )}

                {/* Mobile Floating Action Buttons */}
                <div className="fixed bottom-6 right-6 lg:hidden flex flex-col gap-3">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="w-14 h-14 bg-[#62397f] text-white rounded-full shadow-2xl flex items-center justify-center active:scale-90 transition-all"
                        >
                            <Edit3 size={24} />
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleCancel}
                                className="w-14 h-14 bg-red-500 text-white rounded-full shadow-2xl flex items-center justify-center active:scale-90 transition-all"
                            >
                                <X size={24} />
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="w-14 h-14 bg-[#62397f] text-white rounded-full shadow-2xl flex items-center justify-center active:scale-90 transition-all disabled:opacity-60"
                            >
                                {isSaving ? <Loader2 size={24} className="animate-spin" /> : <Save size={24} />}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
